import type { ReactElement } from 'react';
import { useT } from '../lib/i18n';

/**
 * The row of tags under a list's filters, and the one that is selected.
 *
 * **Computed over the list that has already arrived**, exactly as Open / Today /
 * All are, and cumulative with them: a personal list is a few dozen rows, and a
 * facet that costs a round trip is a facet that shows a spinner for a question
 * already answered on screen.
 *
 * A component rather than a copy on each screen, for the reason `Panel` is one:
 * two lists each with a private version is how the facet of one comes to sit two
 * pixels off the facet of the other.
 *
 * **Nothing at all below two tags.** One tag and "All" is a control with a single
 * meaningful state — everything it could hide is already on screen. The exception
 * is a tag that is currently selected and whose last row has just left the list:
 * the caller keeps it in `tags` and the row stays, or the selection would be
 * impossible to lift.
 */
export function TagFacet({
  tags,
  selected,
  onSelect,
}: {
  tags: string[];
  selected: string | null;
  onSelect: (tag: string | null) => void;
}): ReactElement | null {
  const t = useT();
  if (tags.length < 2 && selected === null) return null;

  return (
    <div
      role="group"
      aria-label={t('tags.filters')}
      className="mt-2 flex flex-wrap items-center gap-1"
    >
      <FacetChip label={t('tags.all')} pressed={selected === null} onClick={() => onSelect(null)} />
      {tags.map((tag) => (
        <FacetChip
          key={tag}
          label={tag}
          pressed={selected === tag}
          // Pressing the selected one lifts it, which is what a person tries
          // before looking for the "All" beside it.
          onClick={() => onSelect(selected === tag ? null : tag)}
        />
      ))}
    </div>
  );
}

/**
 * `aria-pressed` and not a tab, for the reason the filters beside it are not
 * tabs: nothing here is a tab panel, and this is a state the list is in rather
 * than a place to be.
 */
function FacetChip({
  label,
  pressed,
  onClick,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`flex min-h-8 items-center rounded-full px-3 text-xs font-medium transition-colors ${
        pressed
          ? 'bg-accent text-accent-ink'
          : 'bg-accent-soft text-accent hover:bg-accent hover:text-accent-ink'
      }`}
    >
      {label}
    </button>
  );
}
